// FireTrainingFLHA.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Save, Send } from 'lucide-react';
import { PDFDocument, StandardFonts } from 'pdf-lib';
import API from './services/api';
import './FireTrainingFLHA.css';

const DRAFT_QUERY_PARAM = 'draft';
const draftStorageKey = (token) => `fireTrainingFLHA:draft:${token}`;
const FLHA_SUBMISSIONS_KEY = 'fireTrainingFLHA:submissions';

const safeJsonParse = (raw, fallback) => {
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const generateSubmissionId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `flha_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};

const uint8ToBase64 = (bytes) => {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

const buildFlhaPdfBase64 = async (formData, station2Users) => {
  const pdfDoc = await PDFDocument.create();
  let page = pdfDoc.addPage();
  const { width, height } = page.getSize();

  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const margin = 48;
  const fontSize = 11;
  const lineHeight = 14;
  const bottomMargin = margin;
  let y = height - margin;

  const startNewPage = () => {
    page = pdfDoc.addPage();
    y = height - margin;
  };

  const ensurePageSpace = (requiredHeight = lineHeight) => {
    if (y - requiredHeight < bottomMargin) {
      startNewPage();
    }
  };

  const addVerticalSpace = (amount = 6) => {
    if (amount <= 0) return;
    ensurePageSpace(amount);
    y -= amount;
  };

  const drawWrappedText = (text, { isBold = false, indent = 0 } = {}) => {
    const content = String(text ?? '').trim();
    if (!content) {
      ensurePageSpace();
      y -= lineHeight;
      return;
    }

    const x = margin + indent;
    const maxWidth = width - margin * 2 - indent;
    const activeFont = isBold ? bold : font;

    const words = content.split(/\s+/).filter(Boolean);
    let line = '';

    for (const word of words) {
      const next = line ? `${line} ${word}` : word;
      const w = activeFont.widthOfTextAtSize(next, fontSize);
      if (w > maxWidth && line) {
        ensurePageSpace();
        page.drawText(line, {
          x,
          y,
          size: fontSize,
          font: activeFont
        });
        y -= lineHeight;
        line = word;
      } else {
        line = next;
      }
    }

    if (line) {
      ensurePageSpace();
      page.drawText(line, {
        x,
        y,
        size: fontSize,
        font: activeFont
      });
      y -= lineHeight;
    }
  };

  const wrapAndDraw = (label, value) => {
    const labelText = `${label}:`;
    const content = String(value ?? '').trim();

    if (!content) {
      drawWrappedText(`${labelText} `);
      return;
    }

    drawWrappedText(`${labelText} ${content}`);
  };

  drawWrappedText('Fire Department Field Training FLHA', { isBold: true });
  addVerticalSpace(6);

  const instructorDisplay = (() => {
    const match = station2Users.find(u => u.email === formData.instructorName);
    if (!match) return formData.instructorName || '';
    return `${match.lastName}, ${match.firstName} [${match.email}]`;
  })();

  wrapAndDraw('Date', formData.date);
  wrapAndDraw('Time', formData.time);
  wrapAndDraw('Instructor', instructorDisplay);
  addVerticalSpace(6);

  wrapAndDraw('Training Location', formData.trainingLocation);
  wrapAndDraw('Third-Party Company / Host FD', formData.thirdPartyCompany);
  wrapAndDraw('Others Attending (Non-DV Firefighters)', formData.othersAttendingTraining);
  addVerticalSpace(6);

  wrapAndDraw('Description of Training Activity', formData.trainingDescription);
  addVerticalSpace(6);
  wrapAndDraw('Emergency Plan', formData.emergencyPlan);
  addVerticalSpace(6);

  drawWrappedText('Firefighters Participating:', { isBold: true });
  const selected = Array.isArray(formData.station2Firefighters) ? formData.station2Firefighters : [];
  if (selected.length === 0) {
    drawWrappedText('None selected');
  } else {
    selected
      .map(email => station2Users.find(u => u.email === email) || { email })
      .map(u => (u.firstName && u.lastName ? `${u.lastName}, ${u.firstName} [${u.email}]` : u.email))
      .forEach(line => drawWrappedText(`- ${line}`, { indent: 12 }));
  }

  addVerticalSpace(6);
  drawWrappedText('No Duff Acknowledgement:', { isBold: true });
  drawWrappedText(formData.noDuffAcknowledgement ? 'Checked' : 'Not checked');
  addVerticalSpace(6);
  drawWrappedText('Final Acknowledgement:', { isBold: true });
  drawWrappedText(formData.finalAcknowledgement ? 'Checked' : 'Not checked');
  addVerticalSpace(6);

  drawWrappedText('Hazards & Controls:', { isBold: true });
  const hazards = formData.hazards || {};
  Object.entries(hazards).forEach(([key, cat]) => {
    if (!cat || !cat.enabled) return;
    drawWrappedText(`${key}:`, { isBold: true });
    const items = Array.isArray(cat.items) ? cat.items : [];
    const controls = Array.isArray(cat.controls) ? cat.controls : [];
    if (items.length) {
      drawWrappedText('Hazards:', { indent: 12, isBold: true });
      items.forEach(h => drawWrappedText(`- ${h}`, { indent: 24 }));
    }
    if (cat.otherHazard && String(cat.otherHazard).trim()) {
      drawWrappedText(`- Other: ${String(cat.otherHazard).trim()}`, { indent: 24 });
    }
    if (controls.length) {
      drawWrappedText('Controls:', { indent: 12, isBold: true });
      controls.forEach(c => drawWrappedText(`- ${c}`, { indent: 24 }));
    }
    if (cat.otherControl && String(cat.otherControl).trim()) {
      drawWrappedText(`- Other: ${String(cat.otherControl).trim()}`, { indent: 24 });
    }
    addVerticalSpace(4);
  });

  const bytes = await pdfDoc.save();
  return uint8ToBase64(bytes);
};

const generateDraftToken = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `draft_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};

const readDraftTokenFromUrl = () => {
  try {
    const url = new URL(window.location.href);
    const token = url.searchParams.get(DRAFT_QUERY_PARAM);
    return token && token.trim() ? token.trim() : null;
  } catch {
    return null;
  }
};

const writeDraftTokenToUrl = (token) => {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(DRAFT_QUERY_PARAM, token);
    window.history.replaceState({}, '', url.toString());
  } catch {
    // no-op
  }
};

const FireTrainingFLHA = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    instructorName: '',
    date: new Date().toISOString().split('T')[0],
    time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
    station2Firefighters: [],
    trainingLocation: '',
    thirdPartyCompany: 'N/A',
    othersAttendingTraining: 'N/A',
    trainingDescription: '',
    emergencyPlan: '',
    hazards: {
      environmental: { enabled: false, items: [], controls: [], otherHazard: '', otherControl: '' },
      heights: { enabled: false, items: [], controls: [], otherHazard: '', otherControl: '' },
      personal: { enabled: false, items: [], controls: [], otherHazard: '', otherControl: '' },
      ergonomic: { enabled: false, items: [], controls: [], otherHazard: '', otherControl: '' },
      material: { enabled: false, items: [], controls: [], otherHazard: '', otherControl: '' },
      workActivity: { enabled: false, items: [], controls: [], otherHazard: '', otherControl: '' },
      electrical: { enabled: false, items: [], controls: [], otherHazard: '', otherControl: '' },
      peopleEquipment: { enabled: false, items: [], controls: [], otherHazard: '', otherControl: '' },
      environmentalImpact: { enabled: false, items: [], controls: [], otherHazard: '', otherControl: '' }
    },
    noDuffAcknowledgement: false,
    finalAcknowledgement: false
  });

  const [statusBanner, setStatusBanner] = useState(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [draftToken, setDraftToken] = useState(null);
  const [isStation2DropdownOpen, setIsStation2DropdownOpen] = useState(false);
  const station2DropdownRef = useRef(null);
  const statusBannerTimeoutRef = useRef(null);

  const [fireDepartmentUsers, setFireDepartmentUsers] = useState([]);

  useEffect(() => {
    const fetchFireDepartmentUsers = async () => {
      try {
        const resp = await API.get('/api/users?departmentId=6');

        let raw = [];
        if (!resp || !resp.data) raw = [];
        else if (Array.isArray(resp.data)) raw = resp.data;
        else if (Array.isArray(resp.data.users)) raw = resp.data.users;
        else if (Array.isArray(resp.data.usersList)) raw = resp.data.usersList;
        else raw = [];

        const normalized = raw
          .map(u => ({
            email: (u.email || u.EMAIL || u.username || u.USERNAME || '').trim(),
            firstName: (u.firstName || u.FIRST_NAME || u.first_name || '').trim(),
            lastName: (u.lastName || u.LAST_NAME || u.last_name || '').trim()
          }))
          .filter(u => u.email);

        normalized.sort((a, b) => {
          const last = String(a.lastName || '').localeCompare(String(b.lastName || ''), undefined, { sensitivity: 'base' });
          if (last !== 0) return last;
          return String(a.firstName || '').localeCompare(String(b.firstName || ''), undefined, { sensitivity: 'base' });
        });

        setFireDepartmentUsers(normalized);
      } catch (error) {
        console.error('Error fetching Fire Department users:', error);
        setFireDepartmentUsers([]);
      }
    };

    fetchFireDepartmentUsers();
  }, []);

  const station2Users = useMemo(() => fireDepartmentUsers, [fireDepartmentUsers]);
  const isBusy = isSavingDraft || isSubmitting;

  const showBanner = (type, message) => {
    if (statusBannerTimeoutRef.current) {
      window.clearTimeout(statusBannerTimeoutRef.current);
    }
    setStatusBanner({ type, message });
    statusBannerTimeoutRef.current = window.setTimeout(() => {
      setStatusBanner(null);
      statusBannerTimeoutRef.current = null;
    }, 4000);
  };

  useEffect(() => {
    return () => {
      if (statusBannerTimeoutRef.current) {
        window.clearTimeout(statusBannerTimeoutRef.current);
      }
    };
  }, []);

  const hazardOptions = {
    environmental: [
      'Weather conditions (heat, cold, wind)',
      'Uneven terrain',
      'Poor visibility',
      'Wildlife/insects',
      'Water hazards'
    ],
    heights: [
      'Ladder operations',
      'Aerial platform work',
      'Roof operations',
      'Fall hazards',
      'Edge protection needed'
    ],
    personal: [
      'Fatigue/impairment',
      'Physical fitness limitations',
      'Medical conditions',
      'Mental fitness concerns',
      'Stress/anxiety'
    ],
    ergonomic: [
      'Heavy lifting',
      'Repetitive motions',
      'Awkward postures',
      'Carrying equipment',
      'Manual handling tasks'
    ],
    material: [
      'Hazardous materials',
      'Fire accelerants',
      'Smoke/fumes',
      'Chemical exposure',
      'Hot surfaces'
    ],
    workActivity: [
      'Live fire training',
      'Vehicle extrication',
      'Search and rescue drills',
      'Confined space entry',
      'Forcible entry operations'
    ],
    electrical: [
      'Power lines overhead',
      'Electrical equipment',
      'Wet conditions with electricity',
      'Emergency lighting setup',
      'Generator operations'
    ],
    peopleEquipment: [
      'Moving vehicles',
      'Multiple crews working',
      'Equipment malfunction',
      'SCBA operations',
      'Radio communication issues'
    ],
    environmentalImpact: [
      'Water runoff',
      'Smoke discharge',
      'Fuel spills',
      'Equipment noise',
      'Public access control'
    ]
  };

  const controlOptions = {
    environmental: [
      'Weather monitoring',
      'Site inspection completed',
      'Lighting provided',
      'Warning markers placed',
      'Safety perimeter established'
    ],
    heights: [
      'Fall protection equipment',
      'Spotters assigned',
      'Ladder inspection completed',
      'Safety harnesses used',
      'Rescue plan in place'
    ],
    personal: [
      'Pre-training fitness check',
      'No Duff policy reviewed',
      'Rest breaks scheduled',
      'Hydration available',
      'Medical support on standby'
    ],
    ergonomic: [
      'Proper lifting techniques reviewed',
      'Team lifting for heavy items',
      'Equipment aids used',
      'Rotation of tasks',
      'Warm-up exercises completed'
    ],
    material: [
      'SDS sheets available',
      'PPE required and inspected',
      'Containment measures',
      'Ventilation adequate',
      'Decontamination procedures'
    ],
    workActivity: [
      'Safety briefing completed',
      'Backup team designated',
      'Communication protocols established',
      'Emergency procedures reviewed',
      'Equipment inspected'
    ],
    electrical: [
      'Electrical hazard assessment',
      'De-energization verified',
      'Safe distances maintained',
      'Insulated tools used',
      'Lockout/tagout procedures'
    ],
    peopleEquipment: [
      'Traffic control measures',
      'High-visibility clothing',
      'Equipment inspection completed',
      'Clear communication channels',
      'Designated safety officer'
    ],
    environmentalImpact: [
      'Containment systems ready',
      'Spill kits available',
      'Drainage protection',
      'Noise mitigation',
      'Public notification completed'
    ]
  };

  const hazardCategories = [
    { key: 'environmental', label: 'Environmental Hazards' },
    { key: 'heights', label: 'Working from Heights Hazards' },
    { key: 'personal', label: 'Personal Limitation Hazards' },
    { key: 'ergonomic', label: 'Ergonomic Hazards' },
    { key: 'material', label: 'Material Hazards' },
    { key: 'workActivity', label: 'Work Activity Hazards' },
    { key: 'electrical', label: 'Electrical Hazards' },
    { key: 'peopleEquipment', label: 'Other People/Equipment Hazards' },
    { key: 'environmentalImpact', label: 'Environmental Impact Hazards' }
  ];

  const toggleStation2User = (email) => {
    setFormData(prev => ({
      ...prev,
      station2Firefighters: prev.station2Firefighters.includes(email)
        ? prev.station2Firefighters.filter(e => e !== email)
        : [...prev.station2Firefighters, email]
    }));
  };

  useEffect(() => {
    const tokenFromUrl = readDraftTokenFromUrl();
    if (!tokenFromUrl) return;

    setDraftToken(tokenFromUrl);

    const raw = localStorage.getItem(draftStorageKey(tokenFromUrl));
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw);
      const saved = parsed && parsed.formData ? parsed.formData : null;
      if (!saved) return;

      setFormData(prev => {
        const next = { ...prev, ...saved };

        // Merge hazards deeply by category so new categories/defaults remain intact.
        const prevHazards = prev.hazards || {};
        const savedHazards = saved.hazards || {};
        const mergedHazards = { ...prevHazards };
        Object.keys(prevHazards).forEach((key) => {
          if (savedHazards[key]) {
            mergedHazards[key] = { ...prevHazards[key], ...savedHazards[key] };
          }
        });
        Object.keys(savedHazards).forEach((key) => {
          if (!mergedHazards[key]) {
            mergedHazards[key] = savedHazards[key];
          }
        });
        next.hazards = mergedHazards;

        return next;
      });
    } catch {
      // ignore malformed drafts
    }
  }, []);

  useEffect(() => {
    if (!isStation2DropdownOpen) return;

    const onMouseDown = (e) => {
      if (!station2DropdownRef.current) return;
      if (!station2DropdownRef.current.contains(e.target)) {
        setIsStation2DropdownOpen(false);
      }
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsStation2DropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isStation2DropdownOpen]);

  const toggleHazardCategory = (category, enabled) => {
    setFormData(prev => ({
      ...prev,
      hazards: {
        ...prev.hazards,
        [category]: {
          ...prev.hazards[category],
          enabled,
          items: enabled ? prev.hazards[category].items : [],
          controls: enabled ? prev.hazards[category].controls : []
        }
      }
    }));
  };

  const toggleHazardItem = (category, item) => {
    setFormData(prev => ({
      ...prev,
      hazards: {
        ...prev.hazards,
        [category]: {
          ...prev.hazards[category],
          items: prev.hazards[category].items.includes(item)
            ? prev.hazards[category].items.filter(i => i !== item)
            : [...prev.hazards[category].items, item]
        }
      }
    }));
  };

  const toggleControlItem = (category, item) => {
    setFormData(prev => ({
      ...prev,
      hazards: {
        ...prev.hazards,
        [category]: {
          ...prev.hazards[category],
          controls: prev.hazards[category].controls.includes(item)
            ? prev.hazards[category].controls.filter(i => i !== item)
            : [...prev.hazards[category].controls, item]
        }
      }
    }));
  };

  const handleSubmit = async (isDraft) => {
    if (isDraft) {
      if (isSavingDraft || isSubmitting) {
        return;
      }

      setIsSavingDraft(true);
      const tokenToUse = draftToken || generateDraftToken();
      const payload = {
        version: 1,
        savedAt: new Date().toISOString(),
        formData
      };

      try {
        localStorage.setItem(draftStorageKey(tokenToUse), JSON.stringify(payload));
        writeDraftTokenToUrl(tokenToUse);
        setDraftToken(tokenToUse);
        showBanner('success', 'FLHA draft saved successfully.');
      } catch (e) {
        // If storage is full/blocked, keep UI behavior but log the error.
        console.error('Failed to save FLHA draft:', e);
        showBanner('error', 'Failed to save FLHA draft. Please try again.');
      } finally {
        setIsSavingDraft(false);
      }

      console.log('Draft saved:', { token: tokenToUse, ...payload });
      return;
    }

    if (isSubmitting || isSavingDraft) {
      return;
    }

    setIsSubmitting(true);

    // Persist a submitted FLHA as a PDF blob (base64) for FLHA History (frontend-only)
    // AND send reverse-ack invitations (tokenized email links) via the backend.
    try {
      const pdfBase64 = await buildFlhaPdfBase64(formData, station2Users);

      const submittedAt = new Date().toISOString();
      const instructorDisplay = (() => {
        const match = station2Users.find(u => u.email === formData.instructorName);
        if (!match) return formData.instructorName || '';
        return `${match.lastName}, ${match.firstName} [${match.email}]`;
      })();

      // 1) Backend submission (authoritative) + tokenized reverse-ack emails
      let submissionId = null;
      try {
        const resp = await API.post('/api/flha/submissions', { formData, pdfBase64 });
        submissionId = resp?.data?.submissionId || null;
      } catch (apiErr) {
        // Keep local fallback so history still shows "current" even if API fails.
        console.error('Failed to create FLHA reverse-ack invitations:', apiErr);
      }

      // 2) Local history fallback/compatibility
      if (!submissionId) {
        const id = generateSubmissionId();
        const nextEntry = {
          id,
          submittedAt,
          instructorName: formData.instructorName,
          instructorDisplay,
          trainingLocation: formData.trainingLocation,
          pdfBase64
        };

        try {
          const existingRaw = localStorage.getItem(FLHA_SUBMISSIONS_KEY);
          const existing = safeJsonParse(existingRaw, []);
          const next = Array.isArray(existing) ? [nextEntry, ...existing] : [nextEntry];
          localStorage.setItem(FLHA_SUBMISSIONS_KEY, JSON.stringify(next));
        } catch (e) {
          console.error('Failed to store FLHA submission in localStorage:', e);
        }
      }

      console.log('Form submitted:', { ...formData, isDraft: false, submissionId });
      navigate('/fire-training-flha-history', {
        state: {
          successMessage: 'FLHA submitted successfully.'
        }
      });
    } catch (e) {
      console.error('Failed to save FLHA PDF submission:', e);
      showBanner('error', 'Failed to submit FLHA. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    const shouldLeave = window.confirm('Cancel this FLHA and leave the page? Any unsaved changes will be lost.');
    if (!shouldLeave) {
      return;
    }

    navigate('/fire-training-flha-history');
  };

  return (
    <div className="flha-container">
      <div className="flha-wrapper">
        <div className="flha-header">
          <h1 className="flha-title">Fire Department Field Training FLHA</h1>
          <p className="flha-subtitle">Site-Specific Hazard Review Form</p>
        </div>

        {statusBanner && (
          <div className={`flha-success ${statusBanner.type === 'error' ? 'flha-success-error' : ''}`}>
            <AlertCircle className="flha-success-icon" />
            <span className="flha-success-text">{statusBanner.message}</span>
          </div>
        )}

        <div className="flha-content">
          {/* Login Information */}
          <section className="flha-section">
            <h2 className="flha-section-title">Training Information</h2>
            <div className="flha-grid-3">
              <div className="flha-field">
                <label className="flha-label">Instructor Name</label>
                <select
                  value={formData.instructorName}
                  onChange={(e) => setFormData(prev => ({ ...prev, instructorName: e.target.value }))}
                  className="flha-input"
                >
                  <option value="">Select an instructor</option>
                  {station2Users.map(u => (
                    <option key={u.email} value={u.email}>
                      {u.lastName}, {u.firstName} [{u.email}]
                    </option>
                  ))}
                </select>
              </div>
              <div className="flha-field">
                <label className="flha-label">Date</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  className="flha-input"
                />
              </div>
              <div className="flha-field">
                <label className="flha-label">Time</label>
                <input
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                  className="flha-input"
                />
              </div>
            </div>
          </section>

          {/* Firefighters Participating */}
          <section className="flha-section">
            <h2 className="flha-section-title">Firefighters Participating</h2>
            
            <div className="flha-stations">
              <div className="flha-station">
                <div className="flha-field">
                  <label className="flha-label">Select participating firefighters</label>
                  <div ref={station2DropdownRef} className="flha-multiselect">
                    <button
                      type="button"
                      className="flha-input flha-multiselect-trigger"
                      aria-haspopup="menu"
                      aria-expanded={isStation2DropdownOpen}
                      onClick={() => setIsStation2DropdownOpen(open => !open)}
                    >
                      <span>
                        {formData.station2Firefighters.length > 0
                          ? `${formData.station2Firefighters.length} selected`
                          : 'Select firefighters'}
                      </span>
                      <span aria-hidden="true">▾</span>
                    </button>

                    {isStation2DropdownOpen && (
                      <div className="flha-multiselect-menu" role="menu">
                        <div className="flha-multiselect-list">
                          {station2Users.map(u => (
                            <label key={u.email} className="flha-checkbox-label" role="menuitemcheckbox" aria-checked={formData.station2Firefighters.includes(u.email)}>
                              <input
                                type="checkbox"
                                checked={formData.station2Firefighters.includes(u.email)}
                                onChange={() => toggleStation2User(u.email)}
                                className="flha-checkbox"
                              />
                              <span className="flha-checkbox-text">
                                {u.lastName}, {u.firstName} [{u.email}]
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Training Details */}
          <section className="flha-section">
            <h2 className="flha-section-title">Training Details</h2>
            <div className="flha-fields">
              <div className="flha-field">
                <label className="flha-label">Training Location</label>
                <input
                  type="text"
                  value={formData.trainingLocation}
                  onChange={(e) => setFormData(prev => ({ ...prev, trainingLocation: e.target.value }))}
                  placeholder="Enter address or GPS location"
                  className="flha-input"
                />
              </div>

              <div className="flha-field">
                <label className="flha-label">Third-Party Company or Host Fire Department</label>
                <input
                  type="text"
                  value={formData.thirdPartyCompany}
                  onChange={(e) => setFormData(prev => ({ ...prev, thirdPartyCompany: e.target.value }))}
                  placeholder="N/A or enter company/department name"
                  className="flha-input"
                />
              </div>

              <div className="flha-field">
                <label className="flha-label">Others Attending Training (Non-DV Firefighters)</label>
                <input
                  type="text"
                  value={formData.othersAttendingTraining}
                  onChange={(e) => setFormData(prev => ({ ...prev, othersAttendingTraining: e.target.value }))}
                  placeholder="N/A or list other attendees"
                  className="flha-input"
                />
              </div>

              <div className="flha-field">
                <label className="flha-label">Description of Training Activity</label>
                <textarea
                  value={formData.trainingDescription}
                  onChange={(e) => setFormData(prev => ({ ...prev, trainingDescription: e.target.value }))}
                  placeholder="Describe SOG, SOP, training activities, etc."
                  rows="3"
                  className="flha-textarea"
                />
              </div>

              <div className="flha-field">
                <label className="flha-label">Emergency Plan</label>
                <textarea
                  value={formData.emergencyPlan}
                  onChange={(e) => setFormData(prev => ({ ...prev, emergencyPlan: e.target.value }))}
                  placeholder="Describe on-site muster location, call-in procedures, etc."
                  rows="3"
                  className="flha-textarea"
                />
              </div>
            </div>
          </section>

          {/* Hazard Categories */}
          {hazardCategories.map(({ key, label }) => (
            <section key={key} className="flha-section">
              <div className="flha-hazard-header">
                <input
                  type="checkbox"
                  id={`${key}-enabled`}
                  checked={formData.hazards[key].enabled}
                  onChange={(e) => toggleHazardCategory(key, e.target.checked)}
                  className="flha-checkbox-large"
                />
                <label htmlFor={`${key}-enabled`} className="flha-hazard-title">
                  {label}
                </label>
              </div>

              {formData.hazards[key].enabled && (
                <div className="flha-hazard-content">
                  <div className="flha-hazard-section">
                    <h3 className="flha-hazard-subtitle">Identified Hazards (check all that apply)</h3>
                    <div className="flha-hazard-list">
                      {hazardOptions[key].map(hazard => (
                        <label key={hazard} className="flha-hazard-item">
                          <input
                            type="checkbox"
                            checked={formData.hazards[key].items.includes(hazard)}
                            onChange={() => toggleHazardItem(key, hazard)}
                            className="flha-checkbox"
                          />
                          <span className="flha-hazard-text">{hazard}</span>
                        </label>
                      ))}
                      <div className="flha-other-field">
                        <input
                          type="text"
                          value={formData.hazards[key].otherHazard}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            hazards: {
                              ...prev.hazards,
                              [key]: { ...prev.hazards[key], otherHazard: e.target.value }
                            }
                          }))}
                          placeholder="Other hazards (specify)"
                          className="flha-input-small"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flha-hazard-section">
                    <h3 className="flha-hazard-subtitle">Hazard Controls (check all that apply)</h3>
                    <div className="flha-hazard-list">
                      {controlOptions[key].map(control => (
                        <label key={control} className="flha-hazard-item">
                          <input
                            type="checkbox"
                            checked={formData.hazards[key].controls.includes(control)}
                            onChange={() => toggleControlItem(key, control)}
                            className="flha-checkbox"
                          />
                          <span className="flha-hazard-text">{control}</span>
                        </label>
                      ))}
                      <div className="flha-other-field">
                        <input
                          type="text"
                          value={formData.hazards[key].otherControl}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            hazards: {
                              ...prev.hazards,
                              [key]: { ...prev.hazards[key], otherControl: e.target.value }
                            }
                          }))}
                          placeholder="Other controls (specify)"
                          className="flha-input-small"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </section>
          ))}

          {/* No Duff Policy */}
          <section className="flha-no-duff">
            <h4 className="flha-no-duff-title">No Duff Policy</h4>
            <p className="flha-no-duff-text">
              At any point during training, 'No Duff, No Duff' may be implemented by ANY member who may be having a medical emergency event, mechanical SCBA failure, or any personal event that needs to be addressed right away. See 'No Duff' Policy for further information if needed.
            </p>
            <p className="flha-no-duff-italic">
              'Employees attending training should be conscious of their physical and mental fitness before attending training. Due to the increased risk posed by fatigue/impairment, employees that are experiencing symptoms of fatigue/impairment should inform supervising officer of the condition to prevent incidents from occurring; ultimately, they may be excused from conducting training or sent home. As training is being conducted, if persons become subject to the symptoms or effects of fatigue/impairment, notify supervising officer and remove persons from training to prevent the further potential of incidents.' - CCOHS & ABOHS
            </p>

            <label className="flha-no-duff-ack">
              <input
                type="checkbox"
                checked={formData.noDuffAcknowledgement}
                onChange={(e) => setFormData(prev => ({ ...prev, noDuffAcknowledgement: e.target.checked }))}
                className="flha-checkbox-large"
              />
              <span className="flha-no-duff-ack-text">
                I acknowledge the participating firefighters understand the No Duff policy was read to them
              </span>
            </label>
          </section>

          {/* Final Acknowledgement */}
          <section className="flha-acknowledgement">
            <h2 className="flha-section-title">Final Acknowledgement</h2>
            <label className="flha-ack-label">
              <input
                type="checkbox"
                checked={formData.finalAcknowledgement}
                onChange={(e) => setFormData(prev => ({ ...prev, finalAcknowledgement: e.target.checked }))}
                className="flha-checkbox-large"
              />
              <div className="flha-ack-content">
                <p className="flha-ack-title">
                  I acknowledge that the participating firefighters understand and reviewed the hazards and controls.
                </p>
                <p className="flha-ack-subtitle">
                  By checking this box, you confirm that all attending firefighters participated in the hazard review and confirmed their understanding of job tasks, hazards, and controls.
                </p>
              </div>
            </label>
          </section>

          {/* Submit Buttons */}
          <div className="flha-buttons">
            <button
              type="button"
              onClick={() => handleSubmit(true)}
              disabled={isBusy}
              className="flha-button flha-button-draft"
            >
              <Save size={20} />
              {isSavingDraft ? 'Saving Draft...' : 'Save as Draft'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={isBusy}
              className="flha-button flha-button-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => handleSubmit(false)}
              disabled={isBusy || !formData.finalAcknowledgement || !formData.noDuffAcknowledgement}
              className="flha-button flha-button-submit"
            >
              <Send size={20} />
              {isSubmitting ? 'Submitting...' : 'Submit Form'}
            </button>
          </div>

          {(!formData.finalAcknowledgement || !formData.noDuffAcknowledgement) && (
            <p className="flha-warning">
              Please complete the acknowledgements before submitting
            </p>
          )}

          <div className="flha-note">
            <p className="flha-note-text">
              <strong>Note:</strong> Upon submission, automatic notifications will be sent to all participating firefighters for reverse acknowledgement. Firefighters will confirm their understanding of hazards and controls, or indicate disagreement which will notify the instructor.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FireTrainingFLHA;